import React, { useEffect, useRef, useState, useTransition, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { success, vector } from "../../assets/img";
import { get } from "../../Services/ApiHandler";
import { URLS } from "../../Services/Constant";
import moment from "moment";
import { Toast } from "../../Component/Toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { OptimizedTable } from "../../Component/Table/OptimizedTable";
import CustomScroll from "../../Component/CustomScroll";
import { MaskIcon } from "../../Component/MaskIcon";

const Sales = () => {
  const [isPending, startTransition] = useTransition();

  const location = useLocation();
  const listData = location.state?.listData;
  const slotId = location.state?.slotId;
  const ID = location.state?.ID;
  const reportName = location.state?.reportName;
  const lot_name = location.state?.lot_name;
  const time = location.state?.time;
  const date = location.state?.date;

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState("");
  const [downSuccess, setDownSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState(listData || []);
  const [search, setSearch] = useState("");

  // Ref to hold the debounce timer
  const debounceRef = useRef(null);

  // ─── API CALL ────────────────────────────────────────────────────────────────
  const fetchList = async (searchValue = "") => {
    const res = await get(
      `${URLS.SALESREPORT}/${reportName}?${ID}&search=${encodeURIComponent(searchValue)}`
    );

    if (res?.status === "success") {
      startTransition(() => {
        setList(res);
      });
    }
  };

  // ─── DEBOUNCED SEARCH HANDLER ─────────────────────────────────────────────
  // Updates the input instantly for a responsive feel,
  // then waits 300ms after the user stops typing before hitting the API.
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearch(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      fetchList(value);
    }, 300);
  }, [reportName, ID]); // re-create only if route params change

  // Cancel any pending debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // ─── RELOAD DETECTION ────────────────────────────────────────────────────────
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem("pageReloaded", "true");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    const wasReloaded = sessionStorage.getItem("pageReloaded");

    if (wasReloaded === "true") {
      fetchList(""); // fetch with empty search on reload
      sessionStorage.removeItem("pageReloaded");
    }
  }, []);

  // ─── TABLE COLUMNS ────────────────────────────────────────────────────────────
  const columns = React.useMemo(
    () => [
      {
        Header: "Date",
        accessor: "date",
        Cell: ({ value }) => <p>{value}</p>,
      },
      {
        Header: "Time",
        accessor: "time",
        Cell: ({ value }) => <p>{value}</p>,
      },
      {
        Header: "Lot",
        accessor: "lot",
        Cell: ({ value }) => <p>{value}</p>,
      },
      {
        Header: "Details",
        accessor: "detail",
        Cell: ({ value }) => {
          const text = value || "";
          const lower = text.toLowerCase();
          let index = -1;

          if (lower.includes("dear")) {
            index = lower.indexOf("dear");
          } else if (lower.includes("kerala")) {
            index = lower.indexOf("kerala");
          }

          let firstLine = text;
          let secondLine = "";

          if (index !== -1) {
            firstLine = text.substring(0, index).trim();
            secondLine = text.substring(index).trim();
          }

          return (
            <div>
              <p>{firstLine}</p>
              {secondLine && <p>{secondLine}</p>}
            </div>
          );
        },
      },
      {
        Header: "Lot No",
        accessor: "my_number",
        Cell: ({ value }) => <p className="text-nowrap">{value}</p>,
      },
      {
        Header: "Qty",
        accessor: "qty",
        Cell: ({ value }) => <p className="text-nowrap">{value}</p>,
      },
      {
        Header: "Price Amount",
        accessor: "fees",
        Cell: ({ value }) => <p className="text-nowrap">{value}</p>,
      },
      {
        Header: () => (
          <div style={{ textAlign: "center" }}>
            Client <br /> ID
          </div>
        ),
        accessor: "client_id",
        Cell: ({ value }) => <p className="text-nowrap">{value}</p>,
      },
      {
        Header: "Result Status",
        accessor: "announce_status",
        Cell: ({ value }) => (
          <div className="d-flex ac-jc">
            <button
              className={`${
                value === "Pending" ? "pending_icon" : "announcement_icon"
              } border-0 fs-sm-10 fs-md-12 fs-lg-14 fs-xl-13`}
            >
              {value}
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // ─── PDF DOWNLOAD ─────────────────────────────────────────────────────────────
  const handleDownload = async () => {
    if (!list?.data?.length) {
      Toast("No data available to download");
      return;
    }

    try {
      setLoading(true);
      await generatePdf(list?.data);
      setDownSuccess(true);
    } catch (error) {
      console.error(error);
      Toast("Failed to download PDF");
    } finally {
      setLoading(false);
    }
  };

  const generatePdf = (rows) => {
    const doc = new jsPDF("p", "mm", "a4");

    const cleanedRows = rows.map((r) => {
      let detail = r?.detail;
      if (typeof detail === "string") {
        detail = detail.replace(/\s*(dear|kerala).*$/i, "").trim();
      }
      return { ...r, detail };
    });

    const finalRows = cleanedRows.length ? cleanedRows : rows;
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.text("Kandha Agency – Sales Report", pageWidth / 2, 15, {
      align: "center",
    });

    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(
      `Date: ${moment(date).format("DD-MM-YYYY") || "All"}  |  Lot: ${
        lot_name || "All"
      }  |  Time: ${time || "All"}`,
      pageWidth / 2,
      22,
      { align: "center" }
    );

    doc.text(
      `Report Generated on: ${moment().format("DD-MM-YYYY hh:mm a")}`,
      pageWidth / 2,
      28,
      { align: "center" }
    );

    doc.setLineWidth(0.5);
    doc.line(10, 32, pageWidth - 10, 32);

    // Table
    autoTable(doc, {
      startY: 36,
      head: [
        ["Time", "Detail", "Lot No", "Qty", "Price", "User ID", "Status"],
      ],
      body: finalRows.map((r) => [
        r.time || "-",
        r.detail || "-",
        r.my_number || "-",
        r.qty || "-",
        r.fees || "-",
        r.client_id || "-",
        r.announce_status || "-",
      ]),
      foot: [
        [
          {
            content: "Total Price",
            colSpan: 4,
            styles: { halign: "right" },
          },
          {
            content: `Rs. ${list?.total_amount ?? 0}`,
            colSpan: 3,
            styles: { halign: "left" },
          },
        ],
      ],
      showFoot: "lastPage",
      styles: {
        fontStyle: "bold",
        textColor: "black",
        fontSize: 10,
        cellPadding: 1,
        overflow: "linebreak",
        lineWidth: 0.1,
        lineColor: [200, 200, 200],
      },
      headStyles: {
        fillColor: [143, 13, 61],   
        textColor: [255, 255, 255],
        lineWidth: 0.5,
        valign: "middle",
        halign: "center",
        fontStyle: "bold",
        fontSize: 11,
      },
      footStyles: {
        fontSize: 12,
        fontStyle: "bold",
        halign: "left",
          fillColor: [220, 180, 195], 
      },
      margin: 5,
    });

    doc.save(
      `KANDHAsalesreport_${moment(date).format("DD-MM-YYYY")}_${time}.pdf`
    );
  };

  // ─── BROWSER HEADER VISIBILITY ────────────────────────────────────────────────
  const [browsHeader, setBrowsHeader] = useState(true);
  const maxHeight = useRef(window.visualViewport?.height || window.innerHeight);
  const isFirstRun = useRef(true);

  useEffect(() => {
    const handleResize = () => {
      const visibleHeight =
        window.visualViewport?.height || window.innerHeight;

      if (isFirstRun.current) {
        isFirstRun.current = false;
        setBrowsHeader(true);
        return;
      }

      if (visibleHeight < maxHeight.current) {
        setBrowsHeader(true);
      } else {
        maxHeight.current = visibleHeight;
        setBrowsHeader(false);
      }
    };

    window.visualViewport?.addEventListener("resize", handleResize);
    return () =>
      window.visualViewport?.removeEventListener("resize", handleResize);
  }, [browsHeader]);

  // ─── RENDER ───────────────────────────────────────────────────────────────────
  return (
    <div className="dashRightView marginScrolls no-overflow d-flex ac-jb flex-column">
      <CustomScroll height="100vh">
        <div className="container_overalls">

          {/* Download icon */}
          <div
            style={{ marginRight: 22, marginBottom: 20 }}
            className="d-flex ac-je"
          >
            {loading ? (
              <span
                className="spinner-border spinner-border-sm"
                style={{ color: "#8F0D3d", width: 12, height: 12 }}
              />
            ) : downSuccess ? (
              <img src={success} height={20} />
            ) : (
              <MaskIcon onClick={handleDownload} src={vector} height={15} />
            )}
          </div>

          {/* Search input — fires API on every keystroke (debounced 300ms) */}
          <div className="form-group col-md-5 text-start my-3">
            <label className="form-label f3 fs-md-14 fs-lg-16 m-0 primary1">
              Search
            </label>
            <div
              className="login-input1 mt-1"
              style={{ display: "flex", alignItems: "center" }}
            >
              <input
                value={search}
                onChange={handleSearchChange}
                type="text"
                placeholder="Search by lot, details, number..."
                className="placholdercolorchanges2 fs-xs-15 fs-sm-12 fs-md-14 fs-lg-16 fs-xl-17 fs-xxl-20"
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  padding: "0 10px",
                  height: 50,
                }}
              />
              {search && (
                <button
                  onClick={() => {
                    setSearch("");
                    fetchList(""); // clear search → fetch all
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    color: "#8F0D3d",
                    fontSize: 20,
                    lineHeight: 1,
                    padding: "0 12px",
                    flexShrink: 0,
                  }}
                  title="Clear"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <OptimizedTable
            columns={columns}
            setPageno={setPage}
            pagecount={pagination}
            data={list?.data}
            show={false}
          />

        </div>
      </CustomScroll>

      {/* Total price footer */}
      <div className="d-flex ac-jb px-3 team_size">
        <p className="f3 fs-sm-14 white">Total Price</p>
        <p className="f3 fs-sm-14 white">{`₹ ${list?.total_amount || 0}`}</p>
      </div>
    </div>
  );
};

export default Sales;